import React, { useState } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { useTimeline } from '../hooks';
import type { TimelineType } from '../api';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  clientId: string;
}

const PAGE_SIZE = 50;

const TYPE_LABEL: Record<Exclude<TimelineType, 'all'>, string> = {
  client: 'Cadastro',
  address: 'Endereços',
  contact: 'Contatos',
  document: 'Documentos',
  responsible: 'Responsável',
  classification: 'Classificação/Potencial',
};

const TYPE_COLOR: Record<Exclude<TimelineType, 'all'>, string> = {
  client: 'bg-purple-100 text-purple-800',
  address: 'bg-blue-100 text-blue-800',
  contact: 'bg-green-100 text-green-800',
  document: 'bg-yellow-100 text-yellow-800',
  responsible: 'bg-pink-100 text-pink-800',
  classification: 'bg-indigo-100 text-indigo-800',
};

/**
 * Timeline consolidada do cliente (SPEC 02 §1 — RN-COM-047, design D5).
 *
 * Consome o endpoint federado `GET /clients/:id/timeline` — o backend
 * agrega em tempo de leitura eventos de várias fontes. Aqui só
 * apresentamos, com filtro por tipo e por período + "carregar mais"
 * (paginação incremental por offset).
 *
 * `availableSources` no response indica quais fontes já estão plugadas
 * (contratos, propostas, OS entram automaticamente à medida que forem
 * implementadas — sem mudança no frontend).
 */
export const ClienteTimeline: React.FC<Props> = ({ clientId }) => {
  const [type, setType] = useState<TimelineType>('all');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [size, setSize] = useState<number>(PAGE_SIZE);

  const params = {
    type,
    size,
    offset: 0,
    from: from || undefined,
    to: to || undefined,
  };
  const { data, isLoading, isError, error } = useTimeline(clientId, params);

  const events = data?.data ?? [];
  const total = data?.pagination.count ?? 0;
  const hasMore = events.length < total;

  return (
    <div className="space-y-3" data-testid="cliente-timeline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock size={18} /> Timeline consolidada
        </h3>
        <div className="text-xs text-gray-500">
          {total > 0 && `${events.length} de ${total} eventos`}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 md:grid-cols-4">
        <AutocompleteSelect
          label="Filtrar por tipo"
          options={[
            { value: 'all', label: 'Todos os eventos' },
            { value: 'client', label: 'Cadastro' },
            { value: 'address', label: 'Endereços' },
            { value: 'contact', label: 'Contatos' },
            { value: 'document', label: 'Documentos' },
            { value: 'responsible', label: 'Responsável comercial' },
            { value: 'classification', label: 'Classificação / Potencial' },
          ]}
          value={type}
          onChange={(v) => {
            setType(v as TimelineType);
            setSize(PAGE_SIZE);
          }}
        />
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-gray-700">De</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setSize(PAGE_SIZE);
            }}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-gray-700">Até</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setSize(PAGE_SIZE);
            }}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        {(from || to || type !== 'all') && (
          <button
            type="button"
            onClick={() => {
              setType('all');
              setFrom('');
              setTo('');
              setSize(PAGE_SIZE);
            }}
            className="self-end rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando eventos…</div>
      ) : isError ? (
        <div className="text-sm text-red-700">Erro: {(error as Error)?.message}</div>
      ) : events.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nenhum evento nesta janela.
        </div>
      ) : (
        <ol className="relative space-y-3 border-l-2 border-gray-200 pl-6">
          {events.map((e) => {
            const typeKey = e.type === 'all' ? 'client' : (e.type as Exclude<TimelineType, 'all'>);
            const label = TYPE_LABEL[typeKey] ?? e.type;
            const color = TYPE_COLOR[typeKey] ?? 'bg-gray-100 text-gray-700';
            return (
              <li
                key={`${e.action}-${e.id}`}
                className="relative rounded-md border border-gray-200 bg-white p-3"
                data-testid={`timeline-event-${e.id}`}
              >
                <span className="absolute -left-[30px] top-4 h-3 w-3 rounded-full border-2 border-white bg-blue-500" />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
                      {label}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{e.title}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(e.eventAt).toLocaleString('pt-BR')}
                    {e.actorName ? ` · ${e.actorName}` : ''}
                  </div>
                </div>
                {e.detail && (
                  <div className="mt-1 text-xs text-gray-600">
                    <span className="text-gray-500">Detalhe:</span> {e.detail}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {hasMore && !isLoading && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => setSize((s) => s + PAGE_SIZE)}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ChevronDown size={14} /> Carregar mais ({total - events.length} restantes)
          </button>
        </div>
      )}
    </div>
  );
};
